<template>
  <div v-if="showPopup && popup" class="landing-popup-overlay" @click="closePopup">
    <div class="landing-popup-container" @click.stop>
      <div class="landing-popup-header">
        <div class="header-content">
          <h2>{{ popup.title }}</h2>
          <p class="subtitle">Watch this quick video to get started</p>
        </div>
        <button class="close-button" @click="closePopup">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <div class="landing-popup-body">
        <div class="video-container" :class="{ 'vertical': popup.is_vertical }">
          <iframe 
            :src="getEmbedUrl(popup.video_url)" 
            frameborder="0" 
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
            allowfullscreen>
          </iframe>
        </div>
      </div>
      
      <div class="landing-popup-footer">
        <button class="btn-primary" @click="closePopup">
          <span>Got it</span>
          <i class="fas fa-check"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/api';

export default {
  name: 'LandingPopup',
  data() {
    return {
      popup: null,
      showPopup: false,
      loading: false,
      error: null,
    };
  },
  mounted() {
    // Load popup data when component mounts
    this.fetchPopupData();
  },
  methods: {
    async fetchPopupData() {
      try {
        this.loading = true;
        // Add the endpoint to the API service
        const response = await apiService.landingPopups.getAll();
        
        if (response.data && response.data.status === 'success' && response.data.data.length > 0) {
          // Get the first active popup
          const activePopups = response.data.data.filter(item => item.is_active === 1);
          if (activePopups.length > 0) {
            this.popup = activePopups[0];
            this.showPopup = true;
          }
        }
      } catch (error) {
        console.error('Error fetching landing popup data:', error);
        this.error = 'Failed to load popup data';
      } finally {
        this.loading = false;
      }
    },
    closePopup() {
      this.showPopup = false;
    },
    getEmbedUrl(url) {
      // Convert YouTube URL to embed URL
      if (url && url.includes('youtu')) {
        // Handle YouTube URLs
        let videoId = '';
        
        if (url.includes('youtube.com/watch')) {
          // Format: https://www.youtube.com/watch?v=VIDEO_ID
          const urlParams = new URLSearchParams(new URL(url).search);
          videoId = urlParams.get('v');
        } else if (url.includes('youtu.be/')) {
          // Format: https://youtu.be/VIDEO_ID
          videoId = url.split('youtu.be/')[1];
        }
        
        if (videoId) {
          return `https://www.youtube.com/embed/${videoId}`;
        }
      }
      
      // Return original URL if not YouTube or can't parse
      return url;
    }
  }
};
</script>

<style scoped>
.landing-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1010;
}

.landing-popup-container {
  background-color: #ffffff;
  border-radius: 16px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  animation: popup-appear 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.landing-popup-header {
  padding: 1.75rem 2rem;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.header-content {
  flex: 1;
}

.header-content h2 {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.3;
}

.subtitle {
  margin: 0.5rem 0 0;
  color: #64748b;
  font-size: 1rem;
}

.close-button {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.2s ease;
  font-size: 1.25rem;
  margin-left: 1rem;
}

.close-button:hover {
  background-color: #f1f5f9;
  color: #1e293b;
  transform: rotate(90deg);
}

.landing-popup-body {
  padding: 2rem;
}

.video-container {
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
  overflow: hidden;
  width: 100%;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.video-container.vertical {
  padding-bottom: 177.78%;
}

.video-container iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 12px;
}

.landing-popup-footer {
  padding: 1.75rem 2rem;
  border-top: 1px solid #f1f5f9;
  display: flex;
  justify-content: center;
}

.btn-primary {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 2.5rem;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  font-size: 1.125rem;
  transition: all 0.2s ease;
  box-shadow: 0 4px 6px -1px rgba(59, 130, 246, 0.2);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 8px -1px rgba(59, 130, 246, 0.3);
}

.btn-primary:active {
  transform: translateY(0);
}

@keyframes popup-appear {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@media (max-width: 640px) {
  .landing-popup-container {
    width: 95%;
    margin: 1rem;
  }
  
  .landing-popup-header {
    padding: 1.25rem 1.5rem;
  }
  
  .header-content h2 {
    font-size: 1.5rem;
  }
  
  .landing-popup-body {
    padding: 1.5rem;
  }
  
  .landing-popup-footer {
    padding: 1.25rem 1.5rem;
  }
  
  .btn-primary {
    padding: 0.75rem 2rem;
    font-size: 1rem;
  }
}
</style> 